/**
 * CookieConsent Banner
 *
 * Shown to users who have not yet made a consent decision.
 * Provides three actions:
 * - Accept All Cookies
 * - Reject Non-Essential
 * - Cookie Settings (opens CookieSettingsDialog)
 *
 * Depends on CookieProvider for consent state management.
 */
import React from 'react';
import { Button, Card, CardContent } from '@xanectech/ui/components';
import { Cookie } from 'lucide-react';
import { useCookieConsent } from './CookieProvider';
import { CookieSettingsDialog } from './CookieSettingsDialog';

interface CookieConsentProps {
  className?: string;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ className }) => {
  const { hasDecided, acceptAll, rejectNonEssential, openSettings } =
    useCookieConsent();

  if (hasDecided) {
    // Still render the settings dialog so it can be opened from e.g. a footer link
    return <CookieSettingsDialog />;
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none ${className ?? ''}`}
        role='region'
        aria-label='Cookie consent'
      >
        <Card className='w-full max-w-2xl pointer-events-auto shadow-2xl border-2 animate-in slide-in-from-bottom-full duration-500'>
          <CardContent className='p-6'>
            <div className='flex items-start gap-4'>
              <div className='flex-shrink-0'>
                <Cookie
                  className='h-6 w-6 text-primary mt-1'
                  aria-hidden='true'
                />
              </div>

              <div className='flex-1 space-y-4'>
                <div className='space-y-2'>
                  <h3 className='text-lg font-semibold text-foreground'>
                    We use cookies to improve your experience
                  </h3>
                  <p className='text-sm text-muted-foreground leading-relaxed'>
                    We use cookies and similar technologies to help personalise
                    content, provide security features, and analyze our traffic.
                    Essential storage is always active. You can choose which
                    optional categories to enable below.
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    For more information, see our{' '}
                    <button
                      onClick={() => window.open('/privacy-policy', '_blank')}
                      className='text-primary hover:underline font-medium'
                    >
                      Privacy Policy
                    </button>{' '}
                    and{' '}
                    <button
                      onClick={() => window.open('/cookie-policy', '_blank')}
                      className='text-primary hover:underline font-medium'
                    >
                      Cookie Policy
                    </button>
                    .
                  </p>
                </div>

                <div className='flex flex-col sm:flex-row gap-3'>
                  <Button
                    onClick={acceptAll}
                    className='flex-1 sm:flex-initial bg-primary hover:bg-primary/90'
                  >
                    Accept All Cookies
                  </Button>
                  <Button
                    onClick={rejectNonEssential}
                    variant='outline'
                    className='flex-1 sm:flex-initial'
                  >
                    Reject Non-Essential
                  </Button>
                  <Button
                    onClick={openSettings}
                    variant='ghost'
                    className='flex-1 sm:flex-initial'
                  >
                    Cookie Settings
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Always render the dialog so it can be opened from the "Cookie Settings" button */}
      <CookieSettingsDialog />
    </>
  );
};

// ---------------------------------------------------------------------------
// Backward-compatible utility exports (kept for any existing call sites)
// ---------------------------------------------------------------------------

export { checkConsentCategory as hasCookieConsent } from './CookieProvider';
export { clearConsent as clearCookieConsent } from './CookieProvider';
